import type { ReactNode } from "react";
import { styled } from "@pigment-css/react";

const Frame = styled("header")<{ placement: "center" | "leading" }>(({ theme }) => ({
  position: "sticky",
  top: 0,
  // Above the in-content menus that scroll up under it (CalendarSelectMenu zIndex 6), matching
  // ShellTopBar — the transparent gradient background otherwise lets them show through.
  zIndex: 7,
  width: "100%",
  boxSizing: "border-box",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  padding: `${theme.fieldSalesTwo.spacing[72]} ${theme.fieldSalesTwo.spacing[16]} ${theme.fieldSalesTwo.spacing[32]}`,
  // Fixed control-row height (48px pill + padding). A taller slot (a TitlePill with a subtitle)
  // overflows into the padding rather than growing the bar and shifting the content beneath it.
  height: `calc(${theme.fieldSalesTwo.spacing[72]} + ${theme.fieldSalesTwo.spacing[48]} + ${theme.fieldSalesTwo.spacing[32]})`,
  background: `linear-gradient(to bottom, ${theme.fieldSalesTwo.colors.background} 34.6%, rgba(240, 238, 237, 0) 100%)`,
  variants: [
    {
      props: { placement: "center" },
      // Sides size to their content (close/back + trailing never shrink/hide); the
      // centre is a capped flexible track so the title can grow only into the space
      // left between them.
      style: { display: "grid", gridTemplateColumns: "auto minmax(0, 1fr) auto" },
    },
    {
      props: { placement: "leading" },
      style: { display: "flex", justifyContent: "space-between" },
    },
  ],
}));

const Start = styled("div")({ gridColumn: 1, justifySelf: "start", display: "flex", alignItems: "center" });

const Center = styled("div")({
  gridColumn: 2,
  justifySelf: "stretch",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 0,
});

const End = styled("div")({ gridColumn: 3, justifySelf: "end", display: "flex", alignItems: "center" });

const LeadingGroup = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  gap: theme.fieldSalesTwo.spacing[10],
}));

const NavTitle = styled("span")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
}));

export interface TopNavProps {
  leading?: ReactNode;
  /** A string renders as centered nav text; pass a node (e.g. <TitlePill/>) for custom title UI. */
  title?: ReactNode;
  trailing?: ReactNode;
  /** "center" (default): title centered between leading/trailing. "leading": title sits next to the leading slot. */
  titlePlacement?: "center" | "leading";
}

export function TopNav({ leading, title, trailing, titlePlacement = "center" }: TopNavProps) {
  const titleNode = typeof title === "string" ? <NavTitle>{title}</NavTitle> : title;

  if (titlePlacement === "leading") {
    return (
      <Frame placement="leading">
        <LeadingGroup>
          {leading}
          {titleNode}
        </LeadingGroup>
        <End>{trailing}</End>
      </Frame>
    );
  }

  return (
    <Frame placement="center">
      <Start>{leading}</Start>
      {titleNode != null && <Center>{titleNode}</Center>}
      <End>{trailing}</End>
    </Frame>
  );
}
